import {
  createRef,
  FC,
  RefObject,
  SyntheticEvent,
  useCallback,
  useId,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { useDocuments } from '@/entities/document';
import { getPersonalIdentifierForms } from '@/entities/personalIdentifiers/helpers/get-forms';
import { useAttributeGroups } from '@/entities/personalIdentifiers/hooks/useAttributeGroups';
import { useNotices } from '@/features/notices/useNotices';
import { EntityForm, FormSections, GroupSection } from '@/shared/components/forms/styled';
import { FormSidebar } from '@/shared/components/layout/FormSidebar';
import { routes } from '@/shared/helpers';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { RefSections, useIntersectionObserver } from '@/shared/hooks/useIntersectionObserver';
import { useFormSave } from '@/shared/lib/hooks/use-form-save';
import { useFormRenderV2 } from '@/shared/lib/hooks/useFormRenderV2';
import { FileUploadingRef } from '@/shared/lib/hooks/useFormRenderV2/components/FileUploading';
import { FieldsArrayRef } from '@/shared/lib/hooks/useFormRenderV2/components/LegalizedCopiesField';
import { CollectionName, isPersonalIdentifierType } from '@/shared/lib/sj-orm';
import { BaseDto } from '@/shared/lib/sj-orm/models/base.dto';
import { PersonalIdentifierDocumentDto } from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { PersonalIdentifierType } from '@/shared/lib/sj-orm/models/personalIdentifiersDocuments/types';
import { CollapseSection } from '@/shared/uikitV2/CollapseSection';
import { getExtractedFormState } from '@/shared/utils/getExtractedFormState';
import { ExtractFormData } from '@/widgets/ExtractFormData';
import { EntitiesToExtract } from '@/widgets/ExtractFormData/types';
import { UploadResult } from '@/widgets/FileUploader/useFiles';
import { useClosingFormConfirm } from '@/widgets/modals/hooks';

import { ALLOWED_FIELDS } from '../../helpers/allowedExtractedFields';
import { personalIdentifiersI18nMap } from '../SelectPidType';

import { PersonalIdentifierFormHeader } from './PersonalIdentifierFormHeader';

type PersonalIdentifierFormProps = {
  category: PersonalIdentifierType;
  defaultValues?: Partial<PersonalIdentifierDocumentDto>;
  mode?: 'save' | 'update';
  title: string;
};

type PersonalIdentifierForm = {
  legalizedCopies: Partial<PersonalIdentifierDocumentDto>[];
};

export const PersonalIdentifierForm: FC<PersonalIdentifierFormProps> = ({
  category,
  defaultValues = {},
  mode = 'save',
  title,
}) => {
  const { t } = useTranslation('common');
  const { t: tEntity } = useTranslation('entities/personalIdentifiers');
  const { getGroups } = useAttributeGroups();
  const isDesktop = useIsDesktop();
  const router = useAppRouter();
  const notices = useNotices();
  const { verifyAiDocument } = useDocuments();

  const groups = getGroups(category);
  const formsArray = getPersonalIdentifierForms(category);
  const [openStates, setOpenStates] = useState<boolean[]>(
    new Array(formsArray?.length ?? 0).fill(true),
  );

  const formId = `personal-identifier-form-id-${useId()}`;

  // States for Extraction
  const [highlightFieldsKeys, setHighlightFieldsKeys] = useState<string[]>([]);

  const [step, setStep] = useState(0);
  const sectionsRef = useRef<RefSections>([]);
  const uploaderRef = useRef<FileUploadingRef>(null);
  const fieldsArrayRefs = useRef<Array<RefObject<FieldsArrayRef>>>([]);
  const { onSubmit: formOnSubmit, finish } = useFormSave({
    collectionName: CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
    updateOrSave: mode,
    initialDtoData: { personalIdentifierType: category },
  });

  const form = useForm<Omit<BaseDto, 'id'>>({
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = async (data: Omit<BaseDto, 'id'>, skipRoute = false) => {
    let uploadedNewFilesResponse: UploadResult | null = null;
    let updatedAttachmentsPaths: string[] | undefined = [];

    if (uploaderRef.current) {
      uploadedNewFilesResponse = await uploaderRef.current.upload?.(true);
      updatedAttachmentsPaths = await uploaderRef.current.updateFilesChanges?.();
    }

    const dto: Partial<PersonalIdentifierDocumentDto> = { ...data };

    if (uploadedNewFilesResponse?.success && updatedAttachmentsPaths !== undefined) {
      dto.attachments = [...updatedAttachmentsPaths, ...uploadedNewFilesResponse.filePaths];
    }

    formOnSubmit(dto);
    const result = await finish(dto);
    if (mode === 'update' && router.query.verify === '1') {
      await verifyAiDocument(result);
    }

    // skip routing when a user is clicking route outside
    // the form (MenuGroups for example) then click save changes
    if (skipRoute) return;
    await router.push(routes.personalId(result?.id));
  };

  // Ask before close the form
  const { handleCancel } = useClosingFormConfirm<Omit<BaseDto, 'id'>>({
    initValues: form.getValues(),
    getCurrentValues: form.getValues,
    onClose: () => router.push(routes.personalIds()),
    onConfirm: onSubmit,
    onValidate: form.trigger,
    formId,
  });

  const onError = () => {
    notices.error(t('formValidationError'));
  };

  useIntersectionObserver({
    sections: sectionsRef.current,
    setStep,
    initProps: { rootMargin: '-10% 0px -90% 0px' },
  });

  if (!formsArray) return null;

  const setItemOpenState = (index: number, open: boolean) => {
    setOpenStates((prev) => prev.map((value, i) => (i === index ? open : value)));
  };

  const handleExtractedData = useCallback(
    (entity: EntitiesToExtract) => {
      // TODO: use type guard after the whole implementing PersonalIdentifierDocumentDtoV2
      if (!('personalIdentifierType' in entity) || mode !== 'save') return;

      if (!isPersonalIdentifierType(entity.personalIdentifierType)) {
        notices.error(tEntity('notices.error'));
        return;
      }

      if (entity.personalIdentifierType !== category) {
        const pidType = tEntity(personalIdentifiersI18nMap[entity.personalIdentifierType]);
        notices.error(tEntity('notices.extractionError', { type: pidType }));
        return;
      }

      const extracted = getExtractedFormState(entity, ALLOWED_FIELDS);
      form.reset(extracted, { keepDirty: false });
      setHighlightFieldsKeys(Object.keys(extracted));
      notices.success(tEntity('notices.success'));
    },
    [tEntity, notices, form],
  );

  return (
    <>
      <EntityForm
        as="form"
        onSubmit={form.handleSubmit((data) => onSubmit(data), onError)}
        noValidate
      >
        <PersonalIdentifierFormHeader
          title={title}
          category={category}
          isSubmitting={form.formState.isSubmitting}
          onCancel={handleCancel}
        />
        <FormSections className="uikitScrollbar">
          {formsArray.map((sectionItems, index) => {
            const currentRef = fieldsArrayRefs.current[index];

            if (fieldsArrayRefs.current.length !== formsArray.length) {
              fieldsArrayRefs.current = formsArray.map(
                (_, i) => fieldsArrayRefs.current[i] || createRef<FieldsArrayRef>(),
              );
            }

            const { Component, sectionItems: sections } = useFormRenderV2({
              form,
              sectionItems,
              defaultValues,
              uploaderRef,
              mode,
              fieldsArrayRef: currentRef,
              highlightFieldsKeys,
            });

            if (!sections) return null;

            const handleAdd = (e: SyntheticEvent) => {
              e.stopPropagation();
              fieldsArrayRefs.current[index]?.current?.handleAdd?.();
            };

            return (
              <CollapseSection
                key={`personal-identifier-section-${index}`}
                index={index}
                setStep={setStep}
                title={groups[index].title}
                withActionButton={groups[index].showActionButton}
                ref={(el) => (sectionsRef.current[index] = el)}
                open={openStates[index]}
                setOpen={(open) => setItemOpenState(index, open)}
                onAdd={handleAdd}
              >
                <GroupSection>
                  {index === 0 && mode === 'save' && (
                    <ExtractFormData
                      subtype={category}
                      entityType="personalIdentifiers"
                      addFiles={(files) => uploaderRef.current?.addFiles?.(files)}
                      setExtractedData={handleExtractedData}
                      style={{ gridColumn: 'span 2' }}
                    />
                  )}
                  {Component}
                </GroupSection>
              </CollapseSection>
            );
          })}
        </FormSections>
      </EntityForm>

      {isDesktop && (
        <FormSidebar
          items={groups}
          ref={sectionsRef}
          step={step}
          setStep={setStep}
          setItemOpenState={setItemOpenState}
        />
      )}
    </>
  );
};
